import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './context/AuthContext';
import LoadingSpinner from './components/common/LoadingSpinner';
import representantAuthService from './services/professionnel/representant-auth.service';

// Lazy loading des pages
const Login = lazy(() => import('./pages/auth/Login'));
const ForgotPasswordPage = lazy(() => import('./pages/auth/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('./pages/auth/ResetPasswordPage'));
// Mai 2026 — SSO SAGeCom → SIGP (chantier CC)
const SsoExchangePage = lazy(() => import('./pages/auth/SsoExchangePage'));
const Dashboard = lazy(() => import('./pages/dashboard/Dashboard'));
const Profile = lazy(() => import('./pages/auth/Profile'));

// Pénal
const PlaintesPage = lazy(() => import('./pages/penal/plaintes/PlaintesPage'));
const PlainteDetail = lazy(() => import('./pages/penal/PlainteDetail'));
const PlaintesPartieCivilePage = lazy(() => import('./pages/penal/plaintes-partie-civile/PlaintesPartieCivilePage'));
const CitationsDirectesPage = lazy(() => import('./pages/penal/citations-directes/CitationsDirectesPage'));
const ScellesPage = lazy(() => import('./pages/penal/scelles/ScellesPage'));
const DenonciationsPage = lazy(() => import('./pages/penal/denonciations/DenonciationsPage'));
const AutoSaisinePage = lazy(() => import('./pages/penal/auto-saisine/AutoSaisinePage'));
const TraitementProcureurPage = lazy(() => import('./pages/penal/traitement-procureur/TraitementProcureurPage'));
const DossiersPage = lazy(() => import('./pages/penal/dossiers/DossiersPage'));
const InstructionPage = lazy(() => import('./pages/penal/instruction/InstructionPage'));
const RegistreSoitTransmisPage = lazy(() => import('./pages/penal/soit-transmis/RegistreSoitTransmisPage'));
const RegistrePreparationAudiencesPage = lazy(() => import('./pages/penal/preparation-audiences/RegistrePreparationAudiencesPage'));
const RegistreAudiencesPenalesPage = lazy(() => import('./pages/penal/registre-audiences/RegistreAudiencesPenalesPage'));

// Public
const DenonciationPubliquePage = lazy(() => import('./pages/public/DenonciationPubliquePage'));
const PlaintePublicPage = lazy(() => import('./pages/public/PlaintePublicPage'));
const DossierCivilPublicPage = lazy(() => import('./pages/public/DossierCivilPublicPage'));
const RolesAudiencesPublicPage = lazy(() => import('./pages/public/RolesAudiencesPublicPage'));
const CalendrierAudiencesPublicPage = lazy(() => import('./pages/public/CalendrierAudiencesPublicPage'));
const GuideProceduresPage = lazy(() => import('./pages/public/GuideProceduresPage'));
const ConsultationPubliquePage = lazy(() => import('./pages/public/ConsultationPubliquePage'));

// Demandes de documents
const DemandeDocumentPage = lazy(() => import('./pages/demandes-documents/DemandeDocumentPage'));
const RegistreDemandesPage = lazy(() => import('./pages/demandes-documents/registre/RegistreDemandesPage'));

// Landing
const LandingPage = lazy(() => import('./pages/landing/LandingPage'));
const SelecteurPaysPage = lazy(() => import('./pages/landing/SelecteurPaysPage'));
const PortailPaysPage = lazy(() => import('./pages/landing/PortailPaysPage'));

// Représentants légaux
const EspaceRepresentantPage = lazy(() => import('./pages/representants/EspaceRepresentantPage'));
const RepresentantFormPage = lazy(() => import('./pages/representants/forms/RepresentantFormPage'));
const RepresentantForgotPasswordPage = lazy(() => import('./pages/professionnel/auth/RepresentantForgotPasswordPage'));
const RepresentantResetPasswordPage = lazy(() => import('./pages/professionnel/auth/RepresentantResetPasswordPage'));

// Espace professionnel — souscription
const SouscriptionPage = lazy(() => import('./pages/professionnel/souscription/SouscriptionPage'));
const DashboardCabinet = lazy(() => import('./pages/professionnel/espace/DashboardCabinet'));
const ChangerMotDePassePage = lazy(() => import('./pages/professionnel/espace/ChangerMotDePassePage'));
const EspaceMembresPage = lazy(() => import('./pages/professionnel/espace/MembresPage'));
const EspaceClientsPage = lazy(() => import('./pages/professionnel/espace/ClientsPage'));
const EspaceDossiersPage = lazy(() => import('./pages/professionnel/espace/DossiersPage'));
const DossiersHuissierPage = lazy(() => import('./pages/professionnel/espace/DossiersHuissierPage'));
const ProcesVerbauxPage = lazy(() => import('./pages/professionnel/espace/ProcesVerbauxPage'));
const TourneesPage = lazy(() => import('./pages/professionnel/espace/TourneesPage'));
const ConfigSagecomPage = lazy(() => import('./pages/professionnel/espace/ConfigSagecomPage'));
const RecouvrementHuissierPage = lazy(() => import('./pages/professionnel/espace/RecouvrementHuissierPage'));
const InsightsHuissierPage = lazy(() => import('./pages/professionnel/espace/InsightsHuissierPage'));
const ActesNotariesPage = lazy(() => import('./pages/professionnel/espace/ActesNotariesPage'));
const RepertoireNotairePage = lazy(() => import('./pages/professionnel/espace/RepertoireNotairePage'));
const AgendaPage = lazy(() => import('./pages/professionnel/espace/AgendaPage'));
const DocumentsPage = lazy(() => import('./pages/professionnel/espace/DocumentsPage'));
const CRAudiencesPage = lazy(() => import('./pages/professionnel/espace/CRAudiencesPage'));
const RecouvrementPage = lazy(() => import('./pages/professionnel/espace/RecouvrementPage'));
const RedactionPage = lazy(() => import('./pages/professionnel/espace/RedactionPage'));
const InsightsPage = lazy(() => import('./pages/professionnel/espace/InsightsPage'));
const FacturationPage = lazy(() => import('./pages/professionnel/espace/FacturationPage'));
const EtatsStatistiquesPage = lazy(() => import('./pages/professionnel/espace/EtatsStatistiquesPage'));
const AnalyseAffairesPage = lazy(() => import('./pages/professionnel/espace/AnalyseAffairesPage'));
const EtatsPage = lazy(() => import('./pages/professionnel/espace/EtatsPage'));
const AssistantIaPage = lazy(() => import('./pages/professionnel/espace/AssistantIaPage'));
const EspaceGuideProceduresPage = lazy(() => import('./pages/professionnel/espace/GuideProceduresPage'));
const AlertesPage = lazy(() => import('./pages/professionnel/espace/AlertesPage'));
const ParametresActesPage = lazy(() => import('./pages/professionnel/espace/ParametresActesPage'));
const ParametresSagecomPage = lazy(() => import('./pages/professionnel/espace/ParametresSagecomPage'));

// Tarifs publics
const TarifsActesPage = lazy(() => import('./pages/public/TarifsActesPage'));

// Civil
const DossiersCivilsPage = lazy(() => import('./pages/civil/dossiers/DossiersCivilsPage'));
const ProceduresUrgencePage = lazy(() => import('./pages/civil/procedures-urgence/ProceduresUrgencePage'));
const EnrolementsPage = lazy(() => import('./pages/civil/enrolements/EnrolementsPage'));
const RegistrePreparationAudiencesCivilesPage = lazy(() => import('./pages/civil/preparation-audiences/RegistrePreparationAudiencesCivilesPage'));
const RegistreAudiencesCivilesPage = lazy(() => import('./pages/civil/registre-audiences/RegistreAudiencesCivilesPage'));
const RegistreApresAudienceCivilePage = lazy(() => import('./pages/civil/apres-audience/RegistreApresAudienceCivilePage'));
const RegistreMiseEnEtatCivilePage = lazy(() => import('./pages/civil/mise-en-etat/RegistreMiseEnEtatCivilePage'));
const RegistreDeliberationCivilePage = lazy(() => import('./pages/civil/deliberation/RegistreDeliberationCivilePage'));
const RegistreQualitesCivilePage = lazy(() => import('./pages/civil/qualites/RegistreQualitesCivilePage'));
const RegistreEnregistrementCivilePage = lazy(() => import('./pages/civil/enregistrement/RegistreEnregistrementCivilePage'));

// Détention
const DetenusPage = lazy(() => import('./pages/detention/DetenusPage'));
const DetenuDetail = lazy(() => import('./pages/detention/DetenuDetail'));
const TransfertsPage = lazy(() => import('./pages/detention/TransfertsPage'));
const MisesEnLibertePage = lazy(() => import('./pages/detention/MisesEnLibertePage'));
const GracesPresidentiellesPage = lazy(() => import('./pages/detention/GracesPresidentiellesPage'));
const VisitesPage = lazy(() => import('./pages/detention/VisitesPage'));

// Administration
const UsersPage = lazy(() => import('./pages/admin/UsersPage'));
const JuridictionsPage = lazy(() => import('./pages/admin/JuridictionsPage'));
const ParametresPage = lazy(() => import('./pages/admin/ParametresPage'));
const MembresPage = lazy(() => import('./pages/admin/MembresPage'));
const PaysListPage = lazy(() => import('./pages/admin/pays/PaysListPage'));
const PaysDetailPage = lazy(() => import('./pages/admin/pays/PaysDetailPage'));
const AdminDashboardPage = lazy(() => import('./pages/admin/AdminDashboardPage'));
const PermissionsPage = lazy(() => import('./pages/admin/PermissionsPage'));
const TemplatesDocumentsPage = lazy(() => import('./pages/admin/TemplatesDocumentsPage'));
const SouscriptionsAdminPage = lazy(() => import('./pages/admin/souscriptions/SouscriptionsAdminPage'));

// Organisation
const ChambresPage = lazy(() => import('./pages/organisation/chambres/ChambresPage'));
const AgentsPage = lazy(() => import('./pages/organisation/agents/AgentsPage'));
const CompositionsPage = lazy(() => import('./pages/organisation/compositions/CompositionsPage'));
const TypesAffairesPage = lazy(() => import('./pages/organisation/types-affaires/TypesAffairesPage'));
const AttributionsPage = lazy(() => import('./pages/organisation/attributions/AttributionsPage'));
const CalendriersAudiencesPage = lazy(() => import('./pages/organisation/calendriers/CalendriersAudiencesPage'));

// Audiences
const RolesAudiencesPage = lazy(() => import('./pages/audiences/RolesAudiencesPage'));

// Composant pour les routes protégées
interface ProtectedRouteProps {
  children: React.ReactElement;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

// Composant pour les routes publiques (redirection si authentifié)
interface PublicRouteProps {
  children: React.ReactElement;
}

const PublicRoute: React.FC<PublicRouteProps> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// Garde de route pour l'espace professionnel (membres de cabinet)
const RequireRepresentant: React.FC<{ children: React.ReactElement }> = ({
  children,
}) => {
  if (!representantAuthService.isAuthenticated()) {
    return <Navigate to="/representants/avocats" replace />;
  }
  return children;
};

function App() {
  return (
    <Router basename="/sigp">
      <Suspense fallback={<LoadingSpinner />}>
        <Routes>
          {/* Routes publiques */}
          <Route
            path="/login"
            element={
              <PublicRoute>
                <Login />
              </PublicRoute>
            }
          />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          {/* Mai 2026 — Endpoint SSO : reçoit ?token=XXX&cabinetSlug=YYY depuis
              SAGeCom, échange le token contre un JWT SIGP, ouvre la session.
              Route publique car l'authentification se fait via le token SSO. */}
          <Route path="/sso" element={<SsoExchangePage />} />

          {/* Routes publiques pour saisie en ligne - ACCESSIBLE SANS AUTHENTIFICATION */}
          <Route
            path="/denonciation-publique"
            element={<DenonciationPubliquePage />}
          />
          <Route
            path="/public/plaintes/nouveau"
            element={<PlaintePublicPage />}
          />
          <Route
            path="/public/civil/nouveau"
            element={<DossierCivilPublicPage />}
          />
          <Route
            path="/public/roles-audiences"
            element={<RolesAudiencesPublicPage />}
          />
          <Route
            path="/public/calendrier-audiences"
            element={<CalendrierAudiencesPublicPage />}
          />
          <Route
            path="/public/guide-procedures"
            element={<GuideProceduresPage />}
          />
          <Route
            path="/consultation-publique"
            element={<ConsultationPubliquePage />}
          />
          <Route
            path="/public/demande-documents"
            element={<DemandeDocumentPage />}
          />
          <Route
            path="/demande-documents"
            element={
              <ProtectedRoute>
                <DemandeDocumentPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/juridiction/:juridictionId/demande-documents"
            element={
              <ProtectedRoute>
                <DemandeDocumentPage />
              </ProtectedRoute>
            }
          />

          {/* Registre des demandes (protégé, filtré par juridiction agent) */}
          <Route
            path="/demandes-documents/registre"
            element={
              <ProtectedRoute>
                <RegistreDemandesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/juridiction/:juridictionId/demandes-documents/registre"
            element={
              <ProtectedRoute>
                <RegistreDemandesPage />
              </ProtectedRoute>
            }
          />

          {/* Routes protégées */}
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            }
          />

          <Route
            path="/juridiction/:juridictionId/dashboard"
            element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            }
          />

          <Route
            path="/profile"
            element={
              <ProtectedRoute>
                <Profile />
              </ProtectedRoute>
            }
          />

          {/* Routes Pénal */}
          <Route
            path="/juridiction/:juridictionId/penal/plaintes"
            element={
              <ProtectedRoute>
                <PlaintesPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/penal/plaintes"
            element={
              <ProtectedRoute>
                <PlaintesPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/penal/plaintes/:id"
            element={
              <ProtectedRoute>
                <PlainteDetail />
              </ProtectedRoute>
            }
          />

          <Route
            path="/juridiction/:juridictionId/penal/plaintes-partie-civile"
            element={
              <ProtectedRoute>
                <PlaintesPartieCivilePage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/penal/plaintes-partie-civile"
            element={
              <ProtectedRoute>
                <PlaintesPartieCivilePage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/juridiction/:juridictionId/penal/citations-directes"
            element={
              <ProtectedRoute>
                <CitationsDirectesPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/penal/citations-directes"
            element={
              <ProtectedRoute>
                <CitationsDirectesPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/juridiction/:juridictionId/penal/scelles"
            element={
              <ProtectedRoute>
                <ScellesPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/penal/scelles"
            element={
              <ProtectedRoute>
                <ScellesPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/juridiction/:juridictionId/penal/denonciations"
            element={
              <ProtectedRoute>
                <DenonciationsPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/penal/denonciations"
            element={
              <ProtectedRoute>
                <DenonciationsPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/juridiction/:juridictionId/penal/auto-saisine"
            element={
              <ProtectedRoute>
                <AutoSaisinePage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/penal/auto-saisine"
            element={
              <ProtectedRoute>
                <AutoSaisinePage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/juridiction/:juridictionId/penal/traitement-procureur"
            element={
              <ProtectedRoute>
                <TraitementProcureurPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/penal/traitement-procureur"
            element={
              <ProtectedRoute>
                <TraitementProcureurPage />
              </ProtectedRoute>
            }
          />

          {/* Routes Workflow Pénal */}
          <Route
            path="/juridiction/:juridictionId/penal/dossiers"
            element={
              <ProtectedRoute>
                <DossiersPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/penal/dossiers"
            element={
              <ProtectedRoute>
                <DossiersPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/juridiction/:juridictionId/penal/instruction"
            element={
              <ProtectedRoute>
                <InstructionPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/penal/instruction"
            element={
              <ProtectedRoute>
                <InstructionPage />
              </ProtectedRoute>
            }
          />

          {/* Route Registre Soit Transmis - Accessible aux OPJ */}
          <Route
            path="/juridiction/:juridictionId/penal/soit-transmis"
            element={
              <ProtectedRoute>
                <RegistreSoitTransmisPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/penal/soit-transmis"
            element={
              <ProtectedRoute>
                <RegistreSoitTransmisPage />
              </ProtectedRoute>
            }
          />

          {/* Route Registre Préparation Audiences - Accessible aux Greffiers */}
          <Route
            path="/juridiction/:juridictionId/penal/preparation-audiences"
            element={
              <ProtectedRoute>
                <RegistrePreparationAudiencesPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/penal/preparation-audiences"
            element={
              <ProtectedRoute>
                <RegistrePreparationAudiencesPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/juridiction/:juridictionId/penal/registre-audiences"
            element={
              <ProtectedRoute>
                <RegistreAudiencesPenalesPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/penal/registre-audiences"
            element={
              <ProtectedRoute>
                <RegistreAudiencesPenalesPage />
              </ProtectedRoute>
            }
          />

          {/* Routes Civil */}
          <Route
            path="/juridiction/:juridictionId/civil/dossiers"
            element={
              <ProtectedRoute>
                <DossiersCivilsPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/civil/dossiers"
            element={
              <ProtectedRoute>
                <DossiersCivilsPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/juridiction/:juridictionId/civil/procedures-urgence"
            element={
              <ProtectedRoute>
                <ProceduresUrgencePage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/civil/procedures-urgence"
            element={
              <ProtectedRoute>
                <ProceduresUrgencePage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/juridiction/:juridictionId/civil/enrolements"
            element={
              <ProtectedRoute>
                <EnrolementsPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/civil/enrolements"
            element={
              <ProtectedRoute>
                <EnrolementsPage />
              </ProtectedRoute>
            }
          />

          {/* Routes Registres Civils */}
          <Route
            path="/juridiction/:juridictionId/civil/preparation-audiences"
            element={
              <ProtectedRoute>
                <RegistrePreparationAudiencesCivilesPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/civil/preparation-audiences"
            element={
              <ProtectedRoute>
                <RegistrePreparationAudiencesCivilesPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/juridiction/:juridictionId/civil/registre-audiences"
            element={
              <ProtectedRoute>
                <RegistreAudiencesCivilesPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/civil/registre-audiences"
            element={
              <ProtectedRoute>
                <RegistreAudiencesCivilesPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/juridiction/:juridictionId/civil/apres-audience"
            element={
              <ProtectedRoute>
                <RegistreApresAudienceCivilePage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/civil/apres-audience"
            element={
              <ProtectedRoute>
                <RegistreApresAudienceCivilePage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/juridiction/:juridictionId/civil/mise-en-etat"
            element={
              <ProtectedRoute>
                <RegistreMiseEnEtatCivilePage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/civil/mise-en-etat"
            element={
              <ProtectedRoute>
                <RegistreMiseEnEtatCivilePage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/juridiction/:juridictionId/civil/deliberation"
            element={
              <ProtectedRoute>
                <RegistreDeliberationCivilePage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/civil/deliberation"
            element={
              <ProtectedRoute>
                <RegistreDeliberationCivilePage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/juridiction/:juridictionId/civil/qualites"
            element={
              <ProtectedRoute>
                <RegistreQualitesCivilePage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/civil/qualites"
            element={
              <ProtectedRoute>
                <RegistreQualitesCivilePage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/juridiction/:juridictionId/civil/enregistrement"
            element={
              <ProtectedRoute>
                <RegistreEnregistrementCivilePage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/civil/enregistrement"
            element={
              <ProtectedRoute>
                <RegistreEnregistrementCivilePage />
              </ProtectedRoute>
            }
          />

          {/* Routes Détention */}
          <Route
            path="/detention/detenus"
            element={
              <ProtectedRoute>
                <DetenusPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/detention/detenus/:id"
            element={
              <ProtectedRoute>
                <DetenuDetail />
              </ProtectedRoute>
            }
          />

          <Route
            path="/detention/transferts"
            element={
              <ProtectedRoute>
                <TransfertsPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/detention/mises-en-liberte"
            element={
              <ProtectedRoute>
                <MisesEnLibertePage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/detention/graces-presidentielles"
            element={
              <ProtectedRoute>
                <GracesPresidentiellesPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/detention/visites"
            element={
              <ProtectedRoute>
                <VisitesPage />
              </ProtectedRoute>
            }
          />

          {/* Routes Administration */}
          <Route
            path="/admin/users"
            element={
              <ProtectedRoute>
                <UsersPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin/juridictions"
            element={
              <ProtectedRoute>
                <JuridictionsPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/juridiction/:juridictionId/admin/membres"
            element={
              <ProtectedRoute>
                <MembresPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/juridiction/:juridictionId/organisation/chambres"
            element={
              <ProtectedRoute>
                <ChambresPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/juridiction/:juridictionId/organisation/agents"
            element={
              <ProtectedRoute>
                <AgentsPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/juridiction/:juridictionId/organisation/compositions"
            element={
              <ProtectedRoute>
                <CompositionsPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/juridiction/:juridictionId/organisation/types-affaires"
            element={
              <ProtectedRoute>
                <TypesAffairesPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/juridiction/:juridictionId/organisation/attributions"
            element={
              <ProtectedRoute>
                <AttributionsPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/juridiction/:juridictionId/organisation/calendriers"
            element={
              <ProtectedRoute>
                <CalendriersAudiencesPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/juridiction/:juridictionId/audiences/roles"
            element={
              <ProtectedRoute>
                <RolesAudiencesPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin/parametres"
            element={
              <ProtectedRoute>
                <ParametresPage />
              </ProtectedRoute>
            }
          />

          {/* Administration multipays — dashboard hub */}
          <Route
            path="/admin"
            element={
              <ProtectedRoute>
                <AdminDashboardPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/pays"
            element={
              <ProtectedRoute>
                <PaysListPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/pays/:id"
            element={
              <ProtectedRoute>
                <PaysDetailPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/permissions"
            element={
              <ProtectedRoute>
                <PermissionsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/templates"
            element={
              <ProtectedRoute>
                <TemplatesDocumentsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/souscriptions"
            element={
              <ProtectedRoute>
                <SouscriptionsAdminPage />
              </ProtectedRoute>
            }
          />

          {/* Page d'accueil = sélecteur de pays (multi-portails) */}
          <Route path="/" element={<SelecteurPaysPage />} />
          <Route path="/accueil" element={<SelecteurPaysPage />} />

          {/* Portail public par pays — chaque pays a son URL et ses personnalisations */}
          <Route path="/portail/:code" element={<PortailPaysPage />} />

          {/* Ancienne landing Congo (rétrocompat) */}
          <Route path="/landing" element={<LandingPage />} />

          {/* Espaces représentants légaux (avocats, notaires, huissiers) */}
          <Route
            path="/representants/mot-de-passe-oublie"
            element={<RepresentantForgotPasswordPage />}
          />
          <Route
            path="/representants/reset-mot-de-passe"
            element={<RepresentantResetPasswordPage />}
          />
          <Route
            path="/representants/:type"
            element={<EspaceRepresentantPage />}
          />
          <Route
            path="/representants/:type/souscription"
            element={<SouscriptionPage />}
          />

          {/* Espace professionnel — espace de gestion du cabinet (protégé) */}
          <Route
            path="/espace-pro"
            element={
              <RequireRepresentant>
                <DashboardCabinet />
              </RequireRepresentant>
            }
          />
          <Route
            path="/espace-pro/changer-mot-de-passe"
            element={
              <RequireRepresentant>
                <ChangerMotDePassePage />
              </RequireRepresentant>
            }
          />
          <Route
            path="/espace-pro/membres"
            element={
              <RequireRepresentant>
                <EspaceMembresPage />
              </RequireRepresentant>
            }
          />
          <Route
            path="/espace-pro/clients"
            element={
              <RequireRepresentant>
                <EspaceClientsPage />
              </RequireRepresentant>
            }
          />
          <Route
            path="/espace-pro/dossiers"
            element={
              <RequireRepresentant>
                <EspaceDossiersPage />
              </RequireRepresentant>
            }
          />
          <Route
            path="/espace-pro/dossiers-huissier"
            element={
              <RequireRepresentant>
                <DossiersHuissierPage />
              </RequireRepresentant>
            }
          />
          <Route
            path="/espace-pro/proces-verbaux"
            element={
              <RequireRepresentant>
                <ProcesVerbauxPage />
              </RequireRepresentant>
            }
          />
          <Route
            path="/espace-pro/tournees"
            element={
              <RequireRepresentant>
                <TourneesPage />
              </RequireRepresentant>
            }
          />
          <Route
            path="/espace-pro/passerelle-sagecom"
            element={
              <RequireRepresentant>
                <ConfigSagecomPage />
              </RequireRepresentant>
            }
          />
          <Route
            path="/espace-pro/recouvrement-huissier"
            element={
              <RequireRepresentant>
                <RecouvrementHuissierPage />
              </RequireRepresentant>
            }
          />
          <Route
            path="/espace-pro/insights-huissier"
            element={
              <RequireRepresentant>
                <InsightsHuissierPage />
              </RequireRepresentant>
            }
          />
          <Route
            path="/espace-pro/actes-notaries"
            element={
              <RequireRepresentant>
                <ActesNotariesPage />
              </RequireRepresentant>
            }
          />
          <Route
            path="/espace-pro/repertoire-notarial"
            element={
              <RequireRepresentant>
                <RepertoireNotairePage />
              </RequireRepresentant>
            }
          />
          <Route
            path="/espace-pro/agenda"
            element={
              <RequireRepresentant>
                <AgendaPage />
              </RequireRepresentant>
            }
          />
          <Route
            path="/espace-pro/documents"
            element={
              <RequireRepresentant>
                <DocumentsPage />
              </RequireRepresentant>
            }
          />
          <Route
            path="/espace-pro/facturation"
            element={
              <RequireRepresentant>
                <FacturationPage />
              </RequireRepresentant>
            }
          />
          <Route
            path="/espace-pro/facturation/etats"
            element={
              <RequireRepresentant>
                <EtatsPage />
              </RequireRepresentant>
            }
          />
          <Route
            path="/espace-pro/etats"
            element={
              <RequireRepresentant>
                <EtatsStatistiquesPage />
              </RequireRepresentant>
            }
          />
          <Route
            path="/espace-pro/analyse-affaires"
            element={
              <RequireRepresentant>
                <AnalyseAffairesPage />
              </RequireRepresentant>
            }
          />
          <Route
            path="/espace-pro/cr-audiences"
            element={
              <RequireRepresentant>
                <CRAudiencesPage />
              </RequireRepresentant>
            }
          />
          <Route
            path="/espace-pro/recouvrement"
            element={
              <RequireRepresentant>
                <RecouvrementPage />
              </RequireRepresentant>
            }
          />
          <Route
            path="/espace-pro/assistant"
            element={
              <RequireRepresentant>
                <AssistantIaPage />
              </RequireRepresentant>
            }
          />
          <Route
            path="/espace-pro/guides"
            element={
              <RequireRepresentant>
                <EspaceGuideProceduresPage />
              </RequireRepresentant>
            }
          />
          <Route
            path="/espace-pro/alertes"
            element={
              <RequireRepresentant>
                <AlertesPage />
              </RequireRepresentant>
            }
          />
          <Route
            path="/espace-pro/parametrage"
            element={
              <RequireRepresentant>
                <ParametresActesPage />
              </RequireRepresentant>
            }
          />
          <Route
            path="/espace-pro/parametres-sagecom"
            element={
              <RequireRepresentant>
                <ParametresSagecomPage />
              </RequireRepresentant>
            }
          />
          <Route
            path="/espace-pro/redaction"
            element={
              <RequireRepresentant>
                <RedactionPage />
              </RequireRepresentant>
            }
          />
          <Route
            path="/espace-pro/insights"
            element={
              <RequireRepresentant>
                <InsightsPage />
              </RequireRepresentant>
            }
          />
          <Route
            path="/representants/:type/formulaires/:formId"
            element={<RepresentantFormPage />}
          />

          {/* Tarifs publics des actes */}
          <Route path="/public/tarifs" element={<TarifsActesPage />} />
          <Route path="/tarifs" element={<TarifsActesPage />} />

          {/* Route 404 */}
          <Route path="*" element={<div className="flex items-center justify-center h-screen"><h1 className="text-2xl font-bold text-gray-700">404 - Page non trouvée</h1></div>} />
        </Routes>
      </Suspense>
    </Router>
  );
}

export default App;
